import Vue from "vue";
import ServiceTicketService from "@/services/serviceTicketService";

const DEFAULT_ROWS_PER_PAGE = 50;

export function createTabState() {
    return {
        tableData: [],
        totalRecords: 0,
        rowsPerPage: DEFAULT_ROWS_PER_PAGE,
        currentPage: 1,
        first: 0,
        sortField: "created_at",
        sortOrder: -1,
        searchText: "",
        isLoading: false,
    };
}

function tabState(state, tab) {
    if (!state.tabs[tab]) {
        Vue.set(state.tabs, tab, createTabState());
    }
    return state.tabs[tab];
}

export const ServiceTicketStore = {
    namespaced: true,
    state: {
        tabs: {
            new: createTabState(),
            review: createTabState(),
            closed: createTabState(),
            all: createTabState(),
            items_to_collect: createTabState(),
            warranty: createTabState(),
        },
        currentTab: "new",
        lastSelectedTicket: "",
    },
    mutations: {
        SET_TABLE_DATA(state, {tab, data}) {
            tabState(state, tab).tableData = data.data || [];
            tabState(state, tab).totalRecords = data.count || 0;
        },
        UPDATE_CURRENT_PAGE(state, {tab, page}) {
            tabState(state, tab).currentPage = page;
        },
        UPDATE_ROWS_PER_PAGE(state, rows) {
            Object.keys(state.tabs).forEach((tab) => {
                tabState(state, tab).rowsPerPage = rows;
            });
        },
        UPDATE_SORT_OPTIONS(state, {tab, field, order}) {
            tabState(state, tab).sortField = field;
            tabState(state, tab).sortOrder = order;
        },
        RESET_FIRST_VALUE(state, {tab}) {
            const currentPage = tabState(state, tab).currentPage;
            const rowsPerPage = tabState(state, tab).rowsPerPage;
            tabState(state, tab).first = currentPage > 0 ? (currentPage - 1) * rowsPerPage : 0;
        },
        RESET_CURRENT_PAGE(state, {tab, page = 1}) {
            tabState(state, tab).currentPage = page;
            tabState(state, tab).first = 0;
        },
        UPDATE_SEARCH_TEXT(state, {tab, searchText}) {
            tabState(state, tab).searchText = searchText;
        },
        UPDATE_CURRENT_TAB(state, currentTab) {
            state.currentTab = currentTab;
        },
        SET_LOADING(state, {tab, isLoading}) {
            tabState(state, tab).isLoading = isLoading;
        },
        SET_LAST_SELECTED_TICKET(state, ticketId) {
            state.lastSelectedTicket = ticketId;
        },
    },
    actions: {
        async fetchTableData({commit, state, dispatch}) {
            const tab = state.currentTab;
            commit("SET_LOADING", {tab, isLoading: true});

            const params = buildParams(tabState(state, tab), tab);

            try {
                const data = await getApiEndpoint()(`?${buildQueryString(params)}`);
                commit("SET_TABLE_DATA", {tab, data});
            } catch (error) {
                commit("SET_TABLE_DATA", {tab, data: {data: [], count: 0}});
                console.error("Error fetching service tickets:", error);
            } finally {
                commit("SET_LOADING", {tab, isLoading: false});
                dispatch("resetFirstValue");
            }
        },
        updateTableSettings({commit, dispatch, state}, {rows, page}) {
            const tab = state.currentTab;
            if (rows !== tabState(state, tab).rowsPerPage) {
                commit("UPDATE_ROWS_PER_PAGE", rows);
                commit("RESET_CURRENT_PAGE", {tab});
            } else {
                commit("UPDATE_CURRENT_PAGE", {tab, page: page + 1});
            }
            return dispatch("fetchTableData");
        },
        updateSortOptions({commit, dispatch, state}, {field, order}) {
            commit("UPDATE_SORT_OPTIONS", {tab: state.currentTab, field, order});
            commit("RESET_CURRENT_PAGE", {tab: state.currentTab});
            return dispatch("fetchTableData");
        },
        updateSearchText({commit, state}, searchText) {
            commit("UPDATE_SEARCH_TEXT", {tab: state.currentTab, searchText});
        },
        search({commit, dispatch, state}, searchText) {
            const tab = state.currentTab;
            commit("UPDATE_SEARCH_TEXT", {tab, searchText});
            commit("RESET_CURRENT_PAGE", {tab});
            return dispatch("fetchTableData");
        },
        updateTab({commit, dispatch, state}, currentTab) {
            if (currentTab === state.currentTab) {
                return dispatch("fetchTableData");
            }
            commit("UPDATE_CURRENT_TAB", currentTab);
            commit("RESET_CURRENT_PAGE", {tab: currentTab});
            return dispatch("fetchTableData");
        },
        refreshCurrentTab({dispatch}) {
            return dispatch("fetchTableData");
        },
        resetFirstValue({commit, state}) {
            commit("RESET_FIRST_VALUE", {tab: state.currentTab});
        },
        resetCurrentPage({commit, state}, page = 1) {
            commit("RESET_CURRENT_PAGE", {tab: state.currentTab, page});
        },
        setLastSelectedTicket({commit}, ticketId) {
            commit("SET_LAST_SELECTED_TICKET", ticketId);
        },
    },
};

function buildQueryString(params) {
    return Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");
}

function buildParams(currentTabState, currentTab) {
    const params = {
        page: currentTabState.currentPage,
        limit: currentTabState.rowsPerPage,
        orderBy: currentTabState.sortField,
        order: currentTabState.sortOrder,
        status: currentTab,
    };

    if (currentTabState.searchText) {
        params.searchTerm = currentTabState.searchText;
    }

    return params;
}

function getApiEndpoint() {
    return ServiceTicketService.searchServiceTickets;
}
